import { Autocomplete, TextField } from "@mui/material";
import { useHuntersListQuery } from "Api/Queries/User/useHuntersListQuery";
import type { HunterOption } from "Components/Admin/Terminals/EditTerminalDialog/editTerminalSchema";
import { useDebounce } from "Hooks/useDebounce";
import type { HunterListDto } from "Infrastructure/Api/Api";
import { useMemo, useState } from "react";
import { Resources, useResource } from "Translations/Resources";

const RESOURCES = Resources.Admin.Terminals.AssignHunter;

const getHunterLabel = ({
  party: { companyName, firstName, lastName },
  login,
}: HunterListDto) => {
  const name = companyName ?? `${firstName ?? ""} ${lastName ?? ""}`.trim();
  return name ? `${name} (${login})` : login;
};

type Props = {
  value: HunterOption | null;
  onChange: (hunter: HunterOption | null) => void;
};

/** Server-side hunter search — the list is too long to ship to the client. */
export const HunterAutocomplete = ({ value, onChange }: Props) => {
  const { t } = useResource();

  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 400);
  const { data: hunters, isLoading } = useHuntersListQuery({
    offset: 0,
    limit: 20,
    search: debouncedSearch || undefined,
  });

  const options = useMemo((): Array<HunterOption> => {
    const fetched = (hunters?.items ?? []).map((hunter) => ({
      partyPublicID: hunter.party.publicID,
      label: getHunterLabel(hunter),
    }));
    // The current hunter may not be in the fetched page — keep it selectable.
    if (
      value &&
      !fetched.some((option) => option.partyPublicID === value.partyPublicID)
    ) {
      return [value, ...fetched];
    }
    return fetched;
  }, [hunters, value]);

  return (
    <Autocomplete
      options={options}
      loading={isLoading}
      value={value}
      onChange={(_, newValue) => onChange(newValue)}
      inputValue={search}
      onInputChange={(_, newValue) => setSearch(newValue)}
      getOptionLabel={(option) => option.label}
      isOptionEqualToValue={(option, selected) =>
        option.partyPublicID === selected.partyPublicID
      }
      filterOptions={(x) => x}
      noOptionsText={t(RESOURCES.NoOptions)}
      renderInput={(params) => (
        <TextField
          {...params}
          label={t(RESOURCES.HunterLabel)}
          placeholder={t(RESOURCES.SearchHint)}
          size="small"
        />
      )}
    />
  );
};
